import ExportFormat from '@app/model/company_hotel/settings/ExportFormat';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import * as z from 'zod/v4';

import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import useAppForm from '@app/site/_form';
import { useSaveCompanyHotelCalendarExportSettingsMutation } from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import CalendarExportRoomNameExportType
	from '@app/model/company_hotel/settings/CalendarExportRoomNameExportType';

type ExportSettingsRowProps = {
	label: React.ReactNode;
	description: string;
	control: React.ReactNode;
};

export type ExportToExcelSettingsFormHandle = {
	reset: () => void;
	submit: () => void;
};

type ExportToExcelSettingsFormProps = {
	companyHotel: CompanyHotel;
	onPendingChange?: (isPending: boolean) => void;
	onSaved?: () => void;
};

const CalendarExportSettingsValidation = z.object({
	format: z.enum(Object.values(ExportFormat)),
	addRoomName: z.enum(Object.values(CalendarExportRoomNameExportType)),
	addCompetitorsRoomName: z.enum(Object.values(CalendarExportRoomNameExportType)),
	addMealType: z.boolean(),
	addMarketDemand: z.boolean(),
});

const FORMAT_OPTIONS = [
	{ id: ExportFormat.XLSX, value: ExportFormat.XLSX, label: 'XLSX' },
	{ id: ExportFormat.CSV, value: ExportFormat.CSV, label: 'CSV (ZIP)' },
];

const ROOM_NAME_EXPORT_TYPE_OPTIONS = [
	{ id: CalendarExportRoomNameExportType.NONE, value: CalendarExportRoomNameExportType.NONE, label: 'None' },
	{ id: CalendarExportRoomNameExportType.ROOM_NAME, value: CalendarExportRoomNameExportType.ROOM_NAME, label: 'Room name' },
	{ id: CalendarExportRoomNameExportType.ROOM_TYPE, value: CalendarExportRoomNameExportType.ROOM_TYPE, label: 'Room type' },
];

const ExportSettingsRow: React.FunctionComponent<ExportSettingsRowProps> = ({ label, description, control }) => (
	<div className="flex items-start justify-between gap-4">
		<div className="min-w-0">
			<p className="text-xs font-medium text-gray-900">{label}</p>
			<p className="mt-0.5 text-[11px] leading-4 text-gray-500">{description}</p>
		</div>
		<div className="flex-none pt-0.5">
			{control}
		</div>
	</div>
);

const ExportToExcelSettingsForm = forwardRef<ExportToExcelSettingsFormHandle, ExportToExcelSettingsFormProps>(({
	companyHotel,
	onPendingChange,
	onSaved,
}, ref) => {
	const toaster = useToaster();
	const saveCalendarExportSettingsMutation = useSaveCompanyHotelCalendarExportSettingsMutation({
		onSuccess: () => {
			toaster.success('Downloads', 'Export settings saved successfully.');
			if (onSaved !== undefined) onSaved();
		},
	});

	const form = useAppForm({
		defaultValues: {
			format: companyHotel.settings.calendarExport.format ?? ExportFormat.XLSX,
			addRoomName: companyHotel.settings.calendarExport.addRoomName,
			addCompetitorsRoomName: companyHotel.settings.calendarExport.addCompetitorsRoomName,
			addMealType: companyHotel.settings.calendarExport.addMealType,
			addMarketDemand: companyHotel.settings.calendarExport.addMarketDemand,
		},
		validators: {
			onChange: CalendarExportSettingsValidation,
		},
		onSubmit: ({ value }) => saveCalendarExportSettingsMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			input: value,
		}).catch((e: string) => toaster.error('Downloads', e)),
	});

	useEffect(() => {
		if (onPendingChange !== undefined) onPendingChange(saveCalendarExportSettingsMutation.isPending);
	}, [onPendingChange, saveCalendarExportSettingsMutation.isPending]);

	useImperativeHandle(ref, () => ({
		reset: () => form.reset(),
		submit: () => {
			void form.handleSubmit();
		},
	}), [form]);

	return (
		<form.AppForm>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
				onClick={(e) => e.stopPropagation()}
				className="space-y-3"
			>
				<div className="space-y-3">
					<form.AppField name="format">
						{(field) => <ExportSettingsRow
							label="Export format"
							description="Use XLSX or CSV for future exports."
							control={<field.SelectField options={FORMAT_OPTIONS} size="medium" optionsSize="medium"
								disabled={saveCalendarExportSettingsMutation.isPending} hideErrorMessage portal />}
						/>}
					</form.AppField>
					<form.Subscribe selector={(state) => state.values.format}>
						{(format) => <p className="text-xs leading-4 text-gray-500" role="status">
							{format === 'CSV'
								? 'One ZIP with four CSV files: Rates and the three comparisons. CSV has no cell colors or comments.'
								: 'One Excel workbook with four sheets. XLSX keeps cell colors and comments.'}
						</p>}
					</form.Subscribe>
					<hr className="border-gray-200" />
					<form.AppField name="addRoomName">
						{(field) => (
							<ExportSettingsRow
								label="Main room column"
								description="Choose which main-property room identifier to include."
								control={<field.SelectField
									options={ROOM_NAME_EXPORT_TYPE_OPTIONS}
									size="medium"
									optionsSize="medium"
									disabled={saveCalendarExportSettingsMutation.isPending}
									hideErrorMessage
									portal
								/>}
							/>
						)}
					</form.AppField>
					<form.AppField name="addCompetitorsRoomName">
						{(field) => (
							<ExportSettingsRow
								label="Competitor room column"
								description="Choose which competitor room identifier to include."
								control={<field.SelectField
									options={ROOM_NAME_EXPORT_TYPE_OPTIONS}
									size="medium"
									optionsSize="medium"
									disabled={saveCalendarExportSettingsMutation.isPending}
									hideErrorMessage
									portal
								/>}
							/>
						)}
					</form.AppField>
					<form.AppField name="addMealType">
						{(field) => (
							<ExportSettingsRow
								label="Meal type"
								description="Include meal plan information where it is available."
								control={<field.SwitchField label="Meal type" disabled={saveCalendarExportSettingsMutation.isPending} />}
							/>
						)}
					</form.AppField>
					<form.AppField name="addMarketDemand">
						{(field) => (
							<ExportSettingsRow
								label="Market demand"
								description="Include market demand. CSV includes the cluster label as text."
								control={<field.SwitchField label="Market demand" disabled={saveCalendarExportSettingsMutation.isPending} />}
							/>
						)}
					</form.AppField>
					{saveCalendarExportSettingsMutation.isError && <p role="alert" className="text-xs text-red-700">
						The settings could not save. Try again.
					</p>}
				</div>
			</form>
		</form.AppForm>
	);
});

ExportToExcelSettingsForm.displayName = 'ExportToExcelSettingsForm';

export default ExportToExcelSettingsForm;
